import { Timezone } from "@/constants/dateTime";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

const parseDateTime = (dateStr: string, timeStr: string): Date => {
  const [hours, minutes, seconds = 0] = timeStr.split(":").map(Number);
  const date = new Date(dateStr);
  date.setHours(hours, minutes, seconds, 0);
  return date;
};

/**
 * Compares two date and time combinations to determine if the first one is greater than or equal to the second one.
 *
 * @param dateStr1 - The first date string in 'YYYY-MM-DD' format.
 * @param dateStr2 - The second date string in 'YYYY-MM-DD' format.
 * @param timeStr1 - The first time string in 'HH:mm' or 'HH:mm:ss' format.
 * @param timeStr2 - The second time string in 'HH:mm' or 'HH:mm:ss' format.
 * @returns `true` if the first date and time is greater than or equal to the second date and time, `false` otherwise.
 * Returns `false` if any of the input strings are empty or null.
 */
export const compareDateTimesGreaterOrEqual = (
  dateStr1: string,
  dateStr2: string,
  timeStr1: string,
  timeStr2: string
): boolean => {
  if (![dateStr1, dateStr2, timeStr1, timeStr2].every(Boolean)) return false;

  const dateTime1 = parseDateTime(dateStr1, timeStr1);
  const dateTime2 = parseDateTime(dateStr2, timeStr2);

  return dateTime1 >= dateTime2;
};

/**
 * Convert a date and time into RFC3339 format with a specific timezone
 * @param dateTime - Date time string in "YYYY-MM-DD HH:mm" format
 * @param timezoneName - Timezone name (e.g., "Asia/Ho_Chi_Minh")
 * @returns RFC3339 formatted string (2006-01-02T15:04:05Z07:00)
 */
export const convertDateTimeLocalFormat = (
  dateStr: string,
  timeStr: string
) => {
  if (!dateStr || !timeStr) return null;

  try {
    const combinedDateTime = `${dateStr} ${timeStr}`;
    const dateTime = dayjs.tz(
      combinedDateTime,
      "YYYY-MM-DD HH:mm",
      Intl.DateTimeFormat().resolvedOptions().timeZone // timezone
    );

    return dateTime.format("YYYY-MM-DDTHH:mm:ssZ");
  } catch (error) {
    console.error("Failed to convert date and time:", error);
    return null;
  }
};

/**
 * Converts a date string ('YYYY-MM-DD') and a time string ('HH:mm') from a specific timezone
 * into an ISO 8601 datetime string with a custom offset (e.g., '-07:00').
 *
 * This is useful when displaying datetimes in the user's local timezone (e.g., Asia/Jayapura),
 * but sending the datetime back to the backend in the original offset format (e.g., '-07:00').
 *
 * @param dateStr - The date string in 'YYYY-MM-DD' format (e.g., '2023-03-23').
 * @param timeStr - The time string in 'HH:mm' format (e.g., '06:40').
 * @param timezone - The user's timezone object, containing IANA timezone code (e.g., 'Asia/Jayapura').
 * @param timeFormat - The target offset string (e.g., '-07:00', 'Z', '+00:00', '+07:00') to apply in output.
 * @returns A datetime string in ISO 8601 format with the specified offset
 *          (e.g., '2023-03-22T14:40:00-07:00'), or `null` if input is incomplete.
 */
export const convertDateTimeFormat = (
  dateStr: string | undefined,
  timeStr: string | undefined,
  timezone?: Timezone,
  timeFormat?: string | null // e.g. 'YYYY-MM-DDTHH:MM:SS+07:00' or 'YYYY-MM-DDTHH:MM:SSZ'
): string | null => {
  if (!dateStr || !timeStr || !timezone) return null;

  if (!timeFormat && dateStr && timeStr) {
    return dayjs.utc(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm").format();
  }

  const offset = extractOffsetFromFormat(timeFormat ?? "");

  if (!offset) {
    // No offset → treat as UTC
    return dayjs.utc(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm").format();
  }

  // Combine the date and time strings as a local datetime in the user's timezone
  const localDateTime = dayjs.tz(
    `${dateStr} ${timeStr}`,
    "YYYY-MM-DD HH:mm",
    timezone.code
  );

  // Otherwise, convert to custom offset (e.g. '+00:00', '+07:00', etc)
  const converted = localDateTime.utc().utcOffset(offset, false);
  return converted.format();
};

/**
 * Extracts timezone offset (e.g., '+07:00', '-08:00', or 'Z') from time format string.
 * Example input: 'YYYY-MM-DDTHH:MM:SS+07:00' → '+07:00'
 */
const extractOffsetFromFormat = (format: string): string | null => {
  const match = /([+-]\d{2}:\d{2}|Z)$/.exec(format);
  return match ? match[1] : null;
};
